import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/lib/auth";
import { hasStoredAccessToken } from "@/lib/auth/biometricUnlock";

import { markWhatsNewSeen, resolveWhatsNew } from "./whatsNew";

/** Resolves whether to cover the app with what's new after login/restore. */
export function useWhatsNewOnLaunch(): {
    ready: boolean;
    visible: boolean;
    dismiss: () => Promise<void>;
} {
    const { isLoading, isAuthenticated } = useAuth();
    const hadTokenOnLaunchRef = useRef(hasStoredAccessToken());
    const [ready, setReady] = useState(false);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        let cancelled = false;

        void (async () => {
            if (!isAuthenticated) {
                if (!cancelled) {
                    setVisible(false);
                    setReady(true);
                }
                return;
            }

            const show = await resolveWhatsNew(
                await hadTokenOnLaunchRef.current,
            );
            if (cancelled) {
                return;
            }
            setVisible(show);
            setReady(true);
        })();

        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, isLoading]);

    const dismiss = useCallback(async () => {
        await markWhatsNewSeen();
        setVisible(false);
    }, []);

    return {
        ready,
        visible,
        dismiss,
    };
}
